import { Button, cn, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Loader2, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { usePosCategoriesQuery } from "../api/queries";
import {
  type ShopBranchOption,
  shopInventoryApi,
} from "../api/client";
import { hasShopFeature } from "../lib/has-shop-feature";
import { useMerchantShops } from "../store/use-merchant-shops";
import { ShopWarehouseLocationPickerRemote } from "./ShopWarehouseLocationPicker";
import {
  CATALOG_FILTER_CATEGORY_FIELD,
  CATALOG_FILTER_Q_FIELD,
} from "./ShopCatalogFilter";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;

/** ຊື່ field ໃນ URL filters — map ເປັນ query branchId ຕອນເອີ້ນ API stock */
export const INVENTORY_FILTER_BRANCH_FIELD = "inventoryBranchId";
export const INVENTORY_FILTER_LOCATION_FIELD = "inventoryLocationId";

function sortCategories<T extends { id: string; name: string }>(
  items: T[],
  topCategoryIds: string[],
): T[] {
  const topIndex = new Map(topCategoryIds.map((id, i) => [id, i]));
  return [...items].sort((a, b) => {
    const ai = topIndex.get(a.id);
    const bi = topIndex.get(b.id);
    if (ai != null && bi != null) return ai - bi;
    if (ai != null) return -1;
    if (bi != null) return 1;
    return a.name.localeCompare(b.name, "lo");
  });
}

export function ShopInventoryFilter() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: INVENTORY_ROUTE });
  const search = (useSearch({
    from: INVENTORY_ROUTE,
  }) ?? {}) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const qFilter = findCondition(filters, CATALOG_FILTER_Q_FIELD);
  const categoryFilter = findCondition(filters, CATALOG_FILTER_CATEGORY_FIELD);
  const branchFilter = findCondition(filters, INVENTORY_FILTER_BRANCH_FIELD);
  const locationFilter = findCondition(filters, INVENTORY_FILTER_LOCATION_FIELD);

  const { currentShop } = useMerchantShops(shopId);
  const warehouseLocationsOn = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_locations",
  );

  const [searchValue, setSearchValue] = useState<string>(
    (qFilter?.value as string) ?? "",
  );

  useEffect(() => {
    setSearchValue((qFilter?.value as string) ?? "");
  }, [qFilter?.value]);

  const debouncedSyncQToUrl = useDebounceCallback((val: string) => {
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    const trimmed = val.trim();
    if (trimmed) {
      nextFilters = upsertCondition(filters, {
        field: CATALOG_FILTER_Q_FIELD,
        op: "contains",
        value: trimmed,
      });
    } else {
      nextFilters = removeConditions(filters, CATALOG_FILTER_Q_FIELD);
    }
    nav({
      to: INVENTORY_ROUTE,
      params: { shopId: shopId ?? "" },
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const categoryIdValue =
    categoryFilter?.value != null && String(categoryFilter.value).trim() !== ""
      ? String(categoryFilter.value)
      : "";

  const branchIdValue =
    branchFilter?.value != null && String(branchFilter.value).trim() !== ""
      ? String(branchFilter.value)
      : "";

  const locationIdValue =
    locationFilter?.value != null && String(locationFilter.value).trim() !== ""
      ? String(locationFilter.value)
      : "";

  const hasFilter =
    !!qFilter?.value ||
    (categoryFilter?.value != null && String(categoryFilter.value) !== "") ||
    (branchFilter?.value != null && String(branchFilter.value) !== "") ||
    (locationFilter?.value != null && String(locationFilter.value) !== "");

  const applyCategoryFilter = (val: string) => {
    if (!shopId) return;
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!val) {
      nextFilters = removeConditions(filters, CATALOG_FILTER_CATEGORY_FIELD);
    } else {
      nextFilters = upsertCondition(filters, {
        field: CATALOG_FILTER_CATEGORY_FIELD,
        op: "eq",
        value: val,
      });
    }
    nav({
      to: INVENTORY_ROUTE,
      params: { shopId },
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const applyBranchFilter = (val: string) => {
    if (!shopId) return;
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!val) {
      nextFilters = removeConditions(filters, INVENTORY_FILTER_BRANCH_FIELD);
      nextFilters = removeConditions(
        nextFilters ?? [],
        INVENTORY_FILTER_LOCATION_FIELD,
      );
    } else {
      nextFilters = upsertCondition(filters, {
        field: INVENTORY_FILTER_BRANCH_FIELD,
        op: "eq",
        value: val,
      });
    }
    nav({
      to: INVENTORY_ROUTE,
      params: { shopId },
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const applyLocationFilter = (val: string) => {
    if (!shopId) return;
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!val) {
      nextFilters = removeConditions(filters, INVENTORY_FILTER_LOCATION_FIELD);
    } else {
      nextFilters = upsertCondition(filters, {
        field: INVENTORY_FILTER_LOCATION_FIELD,
        op: "eq",
        value: val,
      });
    }
    nav({
      to: INVENTORY_ROUTE,
      params: { shopId },
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const categoriesQuery = usePosCategoriesQuery(
    shopId,
    branchIdValue || undefined,
  );
  const topCategoryIds = categoriesQuery.data?.topCategoryIds ?? [];
  const sortedCategories = useMemo(
    () => sortCategories(categoriesQuery.data?.items ?? [], topCategoryIds),
    [categoriesQuery.data?.items, topCategoryIds],
  );
  const topCategorySet = useMemo(
    () => new Set(topCategoryIds),
    [topCategoryIds],
  );

  if (!shopId) return null;

  return (
    <div className="mb-2 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ: ຊື່, barcode, OEM..."
            value={searchValue}
            onChange={(e) => {
              const v = e.target.value;
              setSearchValue(v);
              debouncedSyncQToUrl(v);
            }}
            className="h-8 sm:max-w-2xs"
          />
          <InfiniteCombobox<ShopBranchOption>
            queryKey={["shops", shopId, "inventory", "branch-lookup"]}
            queryFn={async ({ search: q, pageParam }) => {
              return shopInventoryApi.lookupBranches(shopId, {
                page: pageParam,
                search: q.trim() || undefined,
                limit: 20,
              });
            }}
            preloadQueryFn={(id) => shopInventoryApi.getBranch(shopId, id)}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={branchIdValue}
            onValueChange={(val) => applyBranchFilter(val)}
            placeholder="ສາຂາ"
            className="h-8 w-full sm:w-52"
            clearable
          />
          {warehouseLocationsOn && branchIdValue ? (
            <ShopWarehouseLocationPickerRemote
              shopId={shopId}
              branchId={branchIdValue}
              value={locationIdValue}
              onValueChange={applyLocationFilter}
              placeholder="ຊ່ອງ"
              className="h-8 w-full sm:w-52"
            />
          ) : null}
        </div>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                to: INVENTORY_ROUTE,
                params: { shopId },
                search: { ...search, filters: undefined, offset: 0 },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        ) : null}
      </div>
      <div className="-mx-1 flex gap-2 overflow-x-auto py-1 [scrollbar-width:thin]">
        <button
          type="button"
          onClick={() => applyCategoryFilter("")}
          className={cn(
            "shrink-0 rounded-full border px-4 py-2 font-medium text-sm transition-colors",
            "min-h-9 touch-manipulation",
            !categoryIdValue
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background text-foreground hover:bg-muted",
          )}
        >
          ທັງໝົດ
        </button>
        {categoriesQuery.isLoading ? (
          <span className="flex items-center gap-2 px-2 text-muted-foreground text-xs">
            <Loader2 className="size-3.5 animate-spin" />
            ໂຫຼດໝວດ…
          </span>
        ) : (
          sortedCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => applyCategoryFilter(cat.id)}
              className={cn(
                "shrink-0 rounded-full border px-4 py-2 font-medium text-sm transition-colors",
                "min-h-9 touch-manipulation",
                categoryIdValue === cat.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-muted",
              )}
            >
              {cat.name}
              <span className="ml-1.5 opacity-70">({cat.count})</span>
              {topCategorySet.has(cat.id) ? (
                <span className="ml-1 rounded bg-secondary/80 px-1 py-0.5 text-[9px] uppercase">
                  ຍອດນິຍົມ
                </span>
              ) : null}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
